import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Avatar, Button, Card, Chip, EmptyState, Icon, Modal, Skeleton, Tabs, fmtNum } from "../components/ui";
import CharacterCard, { Cover } from "../components/character/Card";
import Grid from "../components/character/Grid";
import TagFilter from "../components/character/TagFilter";
import SwipeDeck from "../components/character/SwipeDeck";
import { FeaturedBanner } from "../components/lux";
import RankingView from "../components/social/RankingView";
import useInfinite from "../components/character/useInfinite";
import { personalize } from "../components/CharacterCard";
import { Section } from "../components/PlaceholderPage";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../services/i18n";
import content from "../services/api-content";
import "../styles/content.css";

const SORTS = [
  { value: "new", label: "ใหม่ล่าสุด" },
  { value: "trending", label: "กำลังมาแรง" },
  { value: "popular", label: "ยอดนิยม" },
  { value: "following", label: "จากคนที่ติดตาม" },
];
const FALLBACK_TAGS = ["ชาย", "หญิง", "ธงเขียว", "ธงเหลือง", "ธงขาว", "ตลก", "นักศึกษา", "วิศวะ", "มาเฟีย", "ไอดอล", "ฮีโร่", "ขุนนาง"];
const PERIODS = [{ value: "day", label: "วันนี้" }, { value: "week", label: "สัปดาห์นี้" }, { value: "all", label: "ตลอดกาล" }];

/** แถวแนวนอน "ยอดนิยม" สลับช่วงเวลา (day/week จาก rankings, all จาก list ยอดนิยม) */
function PopularRow({ userName }) {
  const [period, setPeriod] = useState("week");
  const { data, loading, error } = useAsync(async () => {
    if (period === "all") return (await content.characters.list({ sort: "popular", limit: 10 })).items;
    const r = await content.rankings(period);
    const rows = r.characters.chats || [];
    return rows.map((x) => ({ ...x, tagline: x.creatorName ? `โดย ${x.creatorName}` : "", messageCount: x.value }));
  }, [period]);
  return (
    <Section title="🔥 ยอดนิยมบน AITalk" action={<div className="chip-row">{PERIODS.map((p) => <Chip key={p.value} size="sm" active={period === p.value} onClick={() => setPeriod(p.value)}>{p.label}</Chip>)}</div>}>
      {error ? <div className="error-text">{error}</div> : loading ? <Skeleton height={230} radius={16} /> : !data.length ? <EmptyState icon="🔥" title="ยังไม่มีข้อมูลในช่วงนี้" /> : (
        <div className="hscroll">{data.map((c) => <CharacterCard key={c.id} c={c} userName={userName} />)}</div>
      )}
    </Section>
  );
}

function CharactersTab() {
  const { profile } = useAuth();
  const { t } = useI18n();
  const [sort, setSort] = useState("trending");
  const [tags, setTags] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [swipeOpen, setSwipeOpen] = useState(false);
  const allTags = useAsync(() => content.tags.list(), []);
  const forYou = useAsync(() => content.characters.list({ sort: "trending", limit: 10 }), []);
  const eventTag = (allTags.data || []).find((x) => x.category === "event");
  const event = useAsync(() => (eventTag ? content.characters.list({ sort: "popular", tags: [eventTag.name], limit: 10 }) : Promise.resolve(null)), [eventTag?.name]);
  const list = useInfinite((page) => content.characters.list({ sort, tags, page, limit: 20 }), [sort, tags.join(",")]);

  const quick = useMemo(() => {
    const all = allTags.data || [];
    const pick = all.filter((x) => ["flag", "type", "role"].includes(x.category)).map((x) => x.name);
    return pick.length ? pick.slice(0, 24) : FALLBACK_TAGS;
  }, [allTags.data]);
  const toggle = (n) => setTags((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));
  const name = profile?.displayName;

  return (
    <>
      <FeaturedBanner items={forYou.data?.items} userName={name} />
      <div className="row mt-16 wrap">
        <Button size="sm" onClick={() => setFilterOpen(true)} aria-label="ตัวกรองแท็ก"><Icon name="settings" size={16} /> ตัวกรอง{tags.length ? ` (${tags.length})` : ""}</Button>
        <Button size="sm" onClick={() => setSwipeOpen(true)}><Icon name="heart" size={16} /> {t("ปัดตัวละคร")}</Button>
        <Link to="/feed" className="btn btn--sm hide-mobile">{t("ฟีดโซเชียล")}</Link>
        <Link to="/shorts" className="btn btn--sm hide-mobile">{t("คลิปสั้น")}</Link>
      </div>

      <Section title={`✨ ${t("สำหรับคุณ")}`}>
        {forYou.error ? <div className="error-text">{forYou.error}</div> : forYou.loading ? <Skeleton height={230} radius={16} /> : (
          <div className="hscroll">{(forYou.data?.items || []).map((c) => <CharacterCard key={c.id} c={c} userName={name} />)}</div>
        )}
      </Section>
      <PopularRow userName={name} />
      {event.data?.items?.length ? (
        <Section title={`🎃 อีเวนต์: ${eventTag.name}`}>
          <div className="hscroll">{event.data.items.map((c) => <CharacterCard key={c.id} c={c} userName={name} />)}</div>
        </Section>
      ) : null}

      <Section title={t("ตัวละครทั้งหมด")}>
        <Tabs tabs={SORTS.map((s) => ({ ...s, label: t(s.label) }))} value={sort} onChange={setSort} />
        <div className="chip-row mt-16" role="group" aria-label="ตัวกรองด่วน">
          {tags.filter((x) => !quick.includes(x)).map((x) => <Chip key={x} active onClick={() => toggle(x)}>{x} ✕</Chip>)}
          {quick.map((x) => <Chip key={x} active={tags.includes(x)} onClick={() => toggle(x)}>{x}</Chip>)}
          {tags.length ? <Chip onClick={() => setTags([])}>{t("ล้างตัวกรอง")} ✕</Chip> : null}
        </div>
        <div className="mt-16">
          <Grid loading={list.loading} error={list.error} onRetry={list.reload} count={list.items.length} hasMore={list.hasMore} onMore={list.loadMore} moreLoading={list.more}
            empty={sort === "following" ? <EmptyState icon="👥" title="ยังไม่มีตัวละครจากคนที่คุณติดตาม">ติดตามครีเอเตอร์ที่ชอบเพื่อให้ตัวละครใหม่มาอยู่ตรงนี้</EmptyState> : undefined}>
            {list.items.map((c) => <CharacterCard key={c.id} c={c} userName={name} />)}
          </Grid>
        </div>
      </Section>

      <TagFilter open={filterOpen} onClose={() => setFilterOpen(false)} tags={allTags.data || []} selected={tags} onChange={setTags} title="ตัวกรองแท็ก" />
      <Modal open={swipeOpen} onClose={() => setSwipeOpen(false)} title={t("ปัดตัวละคร")}><SwipeDeck userName={name} /></Modal>
    </>
  );
}

function UniversesTab() {
  const { t } = useI18n();
  const [sort, setSort] = useState("trending");
  const [q, setQ] = useState("");
  const list = useInfinite((page) => content.universes.list({ sort, q, page, limit: 20 }), [sort, q]);
  return (
    <div className="mt-16">
      <div className="row wrap mb-16">
        <div className="grow" style={{ minWidth: 200 }}><div className="searchbox"><Icon name="search" size={18} className="icon" /><input className="field__control" type="search" aria-label="ค้นหาจักรวาล" placeholder="ค้นหาจักรวาล" value={q} onChange={(e) => setQ(e.target.value)} /></div></div>
        <Link to="/create?type=universe" className="btn btn--primary">+ {t("สร้างจักรวาล")}</Link>
      </div>
      <Tabs tabs={SORTS.filter((s) => s.value !== "following").map((s) => ({ ...s, label: t(s.label) }))} value={sort} onChange={setSort} />
      <div className="mt-16">
        <Grid loading={list.loading} error={list.error} onRetry={list.reload} count={list.items.length} hasMore={list.hasMore} onMore={list.loadMore} moreLoading={list.more}
          empty={<EmptyState icon="🌍" title="ยังไม่มีจักรวาล" action={<Link to="/create?type=universe" className="btn btn--primary">สร้างจักรวาลแรก</Link>}>รวมตัวละครหลายตัวไว้ในโลกเดียวกัน</EmptyState>}>
          {list.items.map((u) => (
            <Link key={u.id} to={`/universe/${u.id}`} className="char-card" aria-label={u.name}>
              <Cover c={{ ...u, emoji: u.emoji || "🌍" }}>
                <span className="char-card__stat"><Icon name="users" size={12} /> {u.characterCount || 0} · <Icon name="chat" size={12} /> {fmtNum(u.messageCount)}</span>
              </Cover>
              <div><div className="char-card__name clamp-1">{u.name}</div><div className="char-card__tag clamp-2">{u.tagline}</div></div>
            </Link>
          ))}
        </Grid>
      </div>
    </div>
  );
}

const CREATOR_SORTS = [["followers", "ผู้ติดตาม"], ["messages", "ข้อความ"], ["chats", "แชท"], ["characters", "ตัวละคร"], ["likes", "ไลก์"]];
const VALUE_KEY = { followers: "followerCount", messages: "messages", chats: "chats", characters: "characters", likes: "likes" };

function CreatorsTab() {
  const [sort, setSort] = useState("followers");
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useAsync(() => content.creators.list({ sort, q }), [sort, q]);
  const unit = CREATOR_SORTS.find((s) => s[0] === sort)[1];
  const val = (c) => fmtNum(c[VALUE_KEY[sort]]);
  const row = (c) => (
    <Link key={c.id} to={`/creator/${c.id}`}>
      <Card link className="row">
        <span className={`rank-n${c.rank <= 3 ? " top" : ""}`}>{c.rank}</span>
        <Avatar name={c.displayName} colors={c.colors} src={c.photoURL} size={48} />
        <div className="grow"><div className="strong clamp-1">{c.displayName}</div><div className="muted clamp-1" style={{ fontSize: 13 }}>@{c.username} · {fmtNum(c.characters)} ตัวละคร</div></div>
        <div className="muted center" style={{ fontSize: 12 }}><b style={{ color: "var(--text)", fontSize: 15 }}>{val(c)}</b><br />{unit}</div>
      </Card>
    </Link>
  );
  return (
    <div className="mt-16">
      <div className="searchbox mb-16"><Icon name="search" size={18} className="icon" /><input className="field__control" type="search" aria-label="ค้นหาครีเอเตอร์" placeholder="ค้นหาครีเอเตอร์" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <div className="chip-row mb-16">{CREATOR_SORTS.map(([v, l]) => <Chip key={v} active={sort === v} onClick={() => setSort(v)}>{l}</Chip>)}</div>
      {error ? <EmptyState icon="⚠️" title="โหลดไม่สำเร็จ" action={<Button onClick={reload}>ลองใหม่</Button>}><span className="error-text">{error}</span></EmptyState>
        : loading ? <div className="stack">{Array.from({ length: 5 }, (_, i) => <Skeleton key={i} height={72} radius={16} />)}</div>
        : !data.items.length ? <EmptyState icon="✍️" title="ไม่พบครีเอเตอร์" />
        : <div className="stack">{data.me && data.me.rank > 10 ? <><div className="faint" style={{ fontSize: 12 }}>อันดับของคุณ</div>{row(data.me)}<hr style={{ borderColor: "var(--border)", width: "100%" }} /></> : null}{data.items.map(row)}</div>}
    </div>
  );
}

export default function Home() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "characters";
  const setTab = (v) => setParams(v === "characters" ? {} : { tab: v }, { replace: true });
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useI18n();
  return (
    <div>
      <div className="row-between wrap mb-16">
        <div>
          <h1>{t("สวัสดี")} {personalize("{{user}}", profile?.displayName || "คุณ")}</h1>
          <p className="muted">{t("วันนี้อยากคุยกับใคร?")}</p>
        </div>
      </div>
      <button className="field__control row mb-16" onClick={() => navigate("/search")} style={{ textAlign: "left", cursor: "text" }} aria-label="ค้นหาตัวละคร">
        <Icon name="search" size={18} /> <span className="faint">{t("ค้นหาตัวละคร ครีเอเตอร์ หรือโค้ดแชร์")}</span>
      </button>
      <Tabs
        tabs={[{ value: "characters", label: t("ตัวละคร") }, { value: "universes", label: t("จักรวาล") }, { value: "creators", label: t("ครีเอเตอร์") }, { value: "ranking", label: t("จัดอันดับ") }]}
        value={tab}
        onChange={setTab}
      />
      {tab === "universes" ? <UniversesTab /> : tab === "creators" ? <CreatorsTab /> : tab === "ranking" ? <div className="mt-16"><RankingView /></div> : <CharactersTab />}
    </div>
  );
}
